import { Head, router, useForm, usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import SuperAdminLayout from './_SuperAdminLayout'
import EmailHealthBanner, { EmailHealth } from './_EmailHealthBanner'
import EmailTestPanel from './_EmailTestPanel'
import EmailTemplateEditor, { EmailTemplateFamily } from './_EmailTemplateEditor'
import EmailReadinessPanel from './_EmailReadinessPanel'
import ConfirmModal from '../../components/shared/ConfirmModal'
import {
  EmailReadiness,
  EmailTestBatchResult,
  EmailTestCatalogEntry,
  SystemEmailSetting,
} from '../../types'

interface EmailEvent {
  id: number
  event_type: string
  email: string
  subject: string | null
  organization_name: string | null
  occurred_at_display: string | null
  reason: string | null
}

interface VerificationResult {
  name: string
  ok: boolean
  detail: string
}

interface VerificationFlash {
  ran_at_display: string
  passed: boolean
  results: VerificationResult[]
}

interface KeyDiagnosticsFlash {
  ran_at_display: string
  status: string
  detail: string
  scopes: string[]
}

interface SharedFlash {
  flash?: {
    notice?: string | null
    alert?: string | null
    verification?: VerificationFlash | null
    key_diagnostics?: KeyDiagnosticsFlash | null
  }
}

interface Props {
  setting: SystemEmailSetting
  recent_events: EmailEvent[]
  email_health: EmailHealth
  webhook_url: string
  retention_days: number
  catalog: EmailTestCatalogEntry[]
  readiness: EmailReadiness
  test_batch: EmailTestBatchResult | null
  test_batches: { id: number; recipient_email: string; created_at_display: string }[]
  current_super_admin_email: string | null
  template_families?: EmailTemplateFamily[]
}

export default function SuperAdminEmailSetting({
  setting,
  recent_events,
  email_health,
  webhook_url,
  retention_days,
  catalog,
  readiness,
  test_batch,
  test_batches,
  current_super_admin_email,
  template_families = [],
}: Props) {
  const { flash } = usePage().props as unknown as SharedFlash
  const verification = flash?.verification ?? null
  const keyDiagnostics = flash?.key_diagnostics ?? null

  const form = useForm({
    system_email_setting: {
      provider: setting.provider || 'sendgrid',
      api_key: '',
      from_email: setting.from_email || '',
      from_name: setting.from_name || '',
      guide_from_email: setting.guide_from_email || '',
      guide_from_name: setting.guide_from_name || '',
      reply_to_email: setting.reply_to_email || '',
      webhook_signing_key: '',
      summary_template_id: setting.summary_template_id || '',
      delivery_mode: setting.delivery_mode,
    },
  })

  const [verifying, setVerifying] = useState(false)
  const [confirmingLive, setConfirmingLive] = useState(false)
  const [diagnosing, setDiagnosing] = useState(false)
  const [helpOpen, setHelpOpen] = useState(!setting.webhook_signing_key_present)
  const visitActive = useRef(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const start = router.on('start', () => { visitActive.current = true; setBusy(true) })
    const finish = router.on('finish', () => { visitActive.current = false; setBusy(false) })
    return () => { start(); finish() }
  }, [])

  const save = (confirmLive = false) => {
    if (visitActive.current) return
    form.transform(data => ({ ...data, confirm_live: confirmLive }))
    form.put('/super_admin/email_setting', {
      preserveScroll: true,
      onError: () => setConfirmingLive(false),
      onSuccess: () => {
        const saved = { ...form.data.system_email_setting, api_key: '', webhook_signing_key: '' }
        form.setData('system_email_setting', saved)
        form.setDefaults({ system_email_setting: saved })
        setConfirmingLive(false)
      },
    })
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (form.data.system_email_setting.delivery_mode === 'live' && setting.delivery_mode !== 'live') {
      setConfirmingLive(true)
    } else {
      save()
    }
  }

  const verifyWebhook = () => {
    if (visitActive.current) return
    setVerifying(true)
    router.post('/super_admin/email_setting/verify_webhook', {}, { preserveScroll: true, onFinish: () => setVerifying(false) })
  }

  const diagnoseKey = () => {
    if (visitActive.current) return
    setDiagnosing(true)
    router.post('/super_admin/email_setting/diagnose_key', {}, { preserveScroll: true, onFinish: () => setDiagnosing(false) })
  }

  // While anything is queued or in flight, keep the per-type results fresh
  // without disturbing the settings form the operator may be editing.
  useEffect(() => {
    if (!test_batch?.in_progress) return
    const id = window.setInterval(() => {
      if (!visitActive.current) router.reload({ only: ['test_batch', 'test_batches', 'readiness', 'setting'] })
    }, 5000)
    return () => window.clearInterval(id)
  }, [test_batch?.in_progress, test_batch?.id])

  const fields = form.data.system_email_setting
  const setField = <K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) => {
    form.setData('system_email_setting', { ...fields, [key]: value })
  }

  return (
    <SuperAdminLayout>
      <Head title="SuperAdmin · Email" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">System Email (SendGrid)</h1>
        <p className="text-sm text-text-muted mt-1">
          Configure the Client/default and Guide sender identities used for transactional email.
          Both addresses must be authorized in SendGrid before they can deliver. Tenants do not manage their own SendGrid keys.
        </p>
      </div>

      {email_health?.status === 'unhealthy' && (
        <EmailHealthBanner health={email_health} className="mb-6" showRecentFailures linkToSettings={false} />
      )}

      <div className="mb-6 border border-stone/20 bg-white rounded-lg p-4 text-sm" role="status">
        <p className="font-semibold text-text-primary">
          Saved delivery mode: {{ off: 'Off', test_only: 'Test only', live: 'Live' }[setting.delivery_mode]}
        </p>
        <p className="text-text-muted mt-0.5">
          {setting.delivery_mode === 'off'
            ? 'Off sends nothing, including operator samples.'
            : setting.delivery_mode === 'test_only'
              ? 'Only explicitly requested Super Admin samples can send real email to the chosen inbox. Customer and automated emails are intentionally blocked.'
              : 'Normal transactional email and operator samples are allowed when configured.'}
          {!setting.configured && ' Configuration is incomplete: save an API key and sender addresses before sending.'}
        </p>
        <p className="text-text-muted mt-1">
          Blocked mail is not queued for release when Live is enabled. Messages already accepted by SendGrid cannot be recalled.
        </p>
      </div>

      {setting.operator_tests_allowed && !setting.webhook_signing_key_present && (
        <div className="mb-6 border border-[#F5C518]/60 bg-[#FFF8E1] rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-[#B8860B] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-text-primary">Webhook verification key is missing.</p>
            <p className="text-[#5C4A1A] mt-0.5">
              Outbound mail will still send, but every inbound SendGrid event webhook will be rejected as
              unauthorized — so deliveries, bounces, and complaints will not be recorded, and test results can
              never move past <span className="font-semibold">Accepted</span>. Open the{' '}
              <button type="button" onClick={() => setHelpOpen(true)} className="underline font-semibold">
                webhook setup guide
              </button>
              {' '}below for step-by-step instructions on copying the key from SendGrid.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={submit} className="bg-white border border-stone/20 rounded-lg p-6">
            <h2 className="text-base font-semibold text-text-primary mb-4">Provider configuration</h2>
            {form.isDirty && <p className="text-sm text-text-primary mb-4" role="status">Unsaved changes. Save settings before testing; readiness and tests use the saved configuration.</p>}
            {Object.values(form.errors).length > 0 && (
              <div role="alert" className="text-sm text-text-primary mb-4">
                {Object.values(form.errors).map((error, index) => <p key={index}>{error}</p>)}
              </div>
            )}

            <div className="space-y-4">
              <Field label="Provider">
                <input
                  type="text"
                  value={fields.provider}
                  disabled
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm bg-[#F5F5F5] text-text-muted"
                />
                <p className="text-xs text-text-muted mt-1">SendGrid is currently the only supported provider.</p>
              </Field>

              <Field label="SendGrid API key">
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={setting.api_key_present ? '•••••••••• (leave blank to keep existing)' : 'SG.xxxxxxxx'}
                  value={fields.api_key}
                  onChange={e => setField('api_key', e.target.value)}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono"
                />
                <p className="text-xs text-text-muted mt-1">
                  Stored encrypted at rest and never displayed again.{' '}
                  {setting.api_key_present
                    ? 'A key is currently on file — leave this blank to keep it, or paste a new one to replace it.'
                    : 'No key on file yet.'}{' '}
                  Only the <span className="font-semibold">Mail Send</span> permission is required.
                </p>
              </Field>

              <div className="border-t border-stone/10 pt-4">
                <h3 className="text-sm font-semibold text-text-primary">Client/default sender</h3>
                <p className="text-xs text-text-muted mt-1 mb-3">Used for client-facing messages and when the audience is uncertain.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Client/default From email">
                  <input
                    type="email"
                    required
                    value={fields.from_email}
                    onChange={e => setField('from_email', e.target.value)}
                    placeholder="Navigator@Pinnacle.Guide"
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                  />
                </Field>
                <Field label="Client/default From name">
                  <input
                    type="text"
                    value={fields.from_name}
                    onChange={e => setField('from_name', e.target.value)}
                    placeholder="Navigator"
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                  />
                </Field>
                </div>
              </div>

              <div className="border-t border-stone/10 pt-4">
                <h3 className="text-sm font-semibold text-text-primary">Guide sender</h3>
                <p className="text-xs text-text-muted mt-1 mb-3">Used for messages specifically concerning Guides.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Guide From email">
                  <input
                    type="email"
                    required
                    value={fields.guide_from_email}
                    onChange={e => setField('guide_from_email', e.target.value)}
                    placeholder="NavigatorSupport@Pinnacle.Guide"
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                  />
                </Field>
                <Field label="Guide From name">
                  <input
                    type="text"
                    value={fields.guide_from_name}
                    onChange={e => setField('guide_from_name', e.target.value)}
                    placeholder="Navigator Support"
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                  />
                </Field>
                </div>
                <p className="text-xs text-text-muted mt-2">Saving these addresses does not verify them. Authorize both senders or their domain in SendGrid before sending.</p>
                {setting.delivery_mode === 'live' && (
                  <p className="text-xs text-text-muted mt-1">
                    Upgraded Live installations keep their previous sender for both audiences until an operator edits them.
                    Verify authorization in SendGrid before changing either address; a saved address alone is not proof of delivery.
                  </p>
                )}
              </div>

              <Field label="Reply-to email (optional)">
                <input
                  type="email"
                  value={fields.reply_to_email}
                  onChange={e => setField('reply_to_email', e.target.value)}
                  placeholder="support@yourdomain.com"
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                />
                <p className="text-xs text-text-muted mt-1">
                  Optional global override for both audiences. When blank, replies go to the selected sender address.
                </p>
              </Field>

              <Field label="Meeting summary dynamic template ID (optional)">
                <input
                  type="text"
                  value={fields.summary_template_id}
                  onChange={e => setField('summary_template_id', e.target.value)}
                  placeholder="d-xxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono"
                />
                <p className="text-xs text-text-muted mt-1">
                  Leave blank to use Pinnacle&apos;s saved, built-in recap HTML and text directly. When set,
                  SendGrid uses that external dynamic template; Pinnacle cannot verify its current subject or
                  markup, and saving an ID here does not publish a version. Configure it with the documented{' '}
                  <code>{'{{subject}}'}</code>, <code>{'{{{recap_html}}}'}</code>, and{' '}
                   <code>{'{{{recap_text}}}'}</code> contract. Download the importable{' '}
                   <a className="underline" href="/super_admin/email_setting/recap_asset?asset=html">HTML asset</a>{' '}
                   and <a className="underline" href="/super_admin/email_setting/recap_asset?asset=text">plain-text asset</a>.
                </p>
              </Field>

              <Field label="Webhook verification key">
                <textarea
                  rows={3}
                  placeholder={
                    setting.webhook_signing_key_present
                      ? '•••••••••• (leave blank to keep existing)'
                      : '-----BEGIN PUBLIC KEY-----\nMFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE...\n-----END PUBLIC KEY-----'
                  }
                  value={fields.webhook_signing_key}
                  onChange={e => setField('webhook_signing_key', e.target.value)}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-xs font-mono"
                />
                <div className="flex items-start justify-between gap-2 mt-1">
                  <p className="text-xs text-text-muted flex-1">
                    ECDSA public key from SendGrid → Mail Settings → Signed Event Webhook. Without it, every
                    inbound webhook is rejected as unauthorized.
                  </p>
                  <button
                    type="button"
                    onClick={() => setHelpOpen(o => !o)}
                    className="text-xs font-semibold text-text-primary underline whitespace-nowrap"
                  >
                    {helpOpen ? 'Hide setup guide' : 'How do I get this?'}
                  </button>
                </div>
              </Field>

              <div>
                <label htmlFor="email-delivery-mode" className="block text-sm font-semibold text-text-primary mb-1">Delivery mode</label>
                <select
                  id="email-delivery-mode"
                  value={fields.delivery_mode}
                  onChange={e => setField('delivery_mode', e.target.value as SystemEmailSetting['delivery_mode'])}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                >
                  <option value="off">Off</option>
                  <option value="test_only">Test only</option>
                  <option value="live">Live</option>
                </select>
                <ul className="text-xs text-text-muted mt-2 space-y-1">
                  <li><strong>Off:</strong> sends nothing, including samples. Credentials can be saved safely.</li>
                  <li><strong>Test only:</strong> sends only explicitly requested Super Admin samples to the chosen inbox. Real SendGrid delivery, not sandbox validation.</li>
                  <li><strong>Live:</strong> allows normal customer and automated transactional email, plus operator samples. Requires confirmation when enabled.</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-stone/10">
              <button
                type="submit"
                disabled={busy || form.processing}
                className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50"
              >
                {form.processing ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>

          {helpOpen && <WebhookSetupGuide webhookUrl={webhook_url} onClose={() => setHelpOpen(false)} />}

          <EmailReadinessPanel readiness={readiness} webhookUrl={webhook_url} />

          <EmailTemplateEditor
            families={template_families}
            busy={busy}
            unsaved={form.isDirty}
            operatorTestsAllowed={setting.operator_tests_allowed}
            superAdminEmail={current_super_admin_email}
            batchId={test_batch?.id ?? null}
          />

          <EmailTestPanel
            history={test_batches}
            busy={busy}
            unsaved={form.isDirty}
            catalog={catalog}
            batch={test_batch}
            setting={setting}
            superAdminEmail={current_super_admin_email}
          />

          <div className="bg-white border border-stone/20 rounded-lg p-6">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-base font-semibold text-text-primary">Local webhook signing checks</h2>
                <p className="text-xs text-text-muted mt-1">
                  Runs four checks against the configured key without contacting SendGrid: that a key is on
                  file, that it decodes as ECDSA, that the verification machinery accepts a known-good
                  signature, and that the webhook endpoint rejects unsigned traffic. Passing all four proves
                  the key is usable — it is not evidence that SendGrid is actually sending you events.
                </p>
              </div>
              <button
                type="button"
                onClick={verifyWebhook}
                disabled={busy || verifying || form.isDirty}
                className="px-4 py-2 bg-[#351E1D] text-white rounded-lg text-sm font-semibold hover:bg-[#4A3B3A] disabled:opacity-50 whitespace-nowrap"
              >
                {verifying ? 'Verifying…' : 'Verify webhook'}
              </button>
            </div>

            {verification && <VerificationResultPanel verification={verification} />}
          </div>

          <div className="bg-white border border-stone/20 rounded-lg p-6">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-base font-semibold text-text-primary">API key diagnostics (optional)</h2>
                <p className="text-xs text-text-muted mt-1">
                  Asks SendGrid which permissions the saved key holds. A least-privilege Mail Send key
                  normally cannot read its own permissions, so an &ldquo;unavailable&rdquo; result is expected
                  and never means sending is broken. The connection test is the authoritative check.
                </p>
              </div>
              <button
                type="button"
                onClick={diagnoseKey}
                disabled={busy || diagnosing || form.isDirty || !setting.api_key_present}
                className="px-4 py-2 bg-white border border-stone/40 text-text-primary rounded-lg text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50 whitespace-nowrap"
              >
                {diagnosing ? 'Checking…' : 'Check key permissions'}
              </button>
            </div>
            {keyDiagnostics && <KeyDiagnosticsPanel diagnostics={keyDiagnostics} />}
          </div>

          <div className="bg-white border border-stone/20 rounded-lg p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h2 className="text-base font-semibold text-text-primary">Recent SendGrid events</h2>
              <span className="text-xs text-text-muted whitespace-nowrap">Retention: {retention_days} days</span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Customer email activity only. Super Admin test emails are kept out of this list and out of the
              email health monitor.
            </p>
            {recent_events.length === 0 ? (
              <div className="py-4 text-center text-sm text-text-muted">No SendGrid events yet.</div>
            ) : (
              <ul className="divide-y divide-stone/10 text-sm">
                {recent_events.map(e => (
                  <li key={e.id} className="py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-text-primary truncate">{e.email}</div>
                      <div className="text-xs text-text-muted flex items-center gap-1 flex-wrap">
                        <EventTypeBadge eventType={e.event_type} />
                        <span>
                          {e.organization_name ? ` · ${e.organization_name}` : ' · system'}
                          {e.subject ? ` · ${e.subject}` : ''}
                        </span>
                      </div>
                      {e.reason && (
                        <div className="text-xs mt-0.5 text-[#ED1C24]">
                          {e.event_type === 'signature_rejected' ? `Signature rejected: ${e.reason}` : e.reason}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-text-muted whitespace-nowrap">{e.occurred_at_display}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <WebhookUrlCard webhookUrl={webhook_url} />

          <div className="bg-white border border-stone/20 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-2">Status</h3>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between gap-3">
                <dt className="text-text-muted">Saved mode</dt>
                <dd className="font-semibold text-text-primary">{{ off: 'Off', test_only: 'Test only', live: 'Live' }[setting.delivery_mode]}</dd>
              </div>
              <Row label="API key" value={setting.api_key_present ? 'On file' : 'Missing'} ok={setting.api_key_present} />
              <Row
                label="Webhook key"
                value={setting.webhook_signing_key_present ? 'On file' : 'Missing'}
                ok={setting.webhook_signing_key_present}
              />
              <Row
                label="Signed events seen"
                value={setting.last_signed_webhook_display || 'Never'}
                ok={!!setting.last_signed_webhook_display}
              />
              <Row label="Last saved" value={setting.updated_at_display || '—'} ok={true} />
            </dl>
          </div>

          <div className="bg-[#FFF8E1] border border-[#F5C518]/40 rounded-lg p-4 text-xs text-[#5C4A1A]">
            <p className="font-semibold mb-1">Heads up</p>
            <p>
              Outbound mail uses the Client/default or Guide sender according to message context.
              Both identities must be authorized in SendGrid; saving them does not verify delivery.
            </p>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmingLive}
        title="Enable Live email delivery?"
        message="Live allows invitations, authentication, billing, recaps, and customer summaries to send to their normal recipients. Blocked mail is not queued for release. Messages already accepted by SendGrid cannot be recalled, even after switching Off."
        confirmLabel="Enable Live and save"
        variant="warning"
        submitting={busy || form.processing}
        onConfirm={() => save(true)}
        onClose={() => setConfirmingLive(false)}
      />
    </SuperAdminLayout>
  )
}

function WebhookUrlCard({ webhookUrl }: { webhookUrl: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopied(true)
      timer.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="bg-white border border-stone/20 rounded-lg p-5">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-text-primary">Webhook URL</h3>
        <button
          type="button"
          onClick={copy}
          className="text-xs font-semibold text-text-primary underline whitespace-nowrap"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-xs text-text-muted mb-2">
        Paste this into SendGrid → Mail Settings → Event Webhook → HTTP POST URL.
      </p>
      <code className="block text-xs bg-[#F5F5F5] p-2 rounded break-all font-mono">{webhookUrl}</code>
    </div>
  )
}

function KeyDiagnosticsPanel({ diagnostics }: { diagnostics: KeyDiagnosticsFlash }) {
  const tone =
    diagnostics.status === 'ok'
      ? 'border-[#15532E]/30 bg-[#E8F5EC]'
      : diagnostics.status === 'error'
        ? 'border-[#ED1C24]/30 bg-[#FDECEC]'
        : 'border-stone/30 bg-[#F5F5F5]'
  const heading =
    diagnostics.status === 'ok'
      ? 'Permissions read'
      : diagnostics.status === 'unavailable'
        ? 'Permissions could not be read (this is normal)'
        : diagnostics.status === 'skipped'
          ? 'Nothing to check'
          : 'Check failed'

  return (
    <div className={`mt-4 border rounded-lg p-4 ${tone}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold text-text-primary">{heading}</p>
        <span className="text-xs text-text-muted">Ran {diagnostics.ran_at_display}</span>
      </div>
      <p className="text-xs text-text-muted">{diagnostics.detail}</p>
      {diagnostics.scopes?.length > 0 && (
        <p className="text-xs text-text-muted mt-2 font-mono break-all">{diagnostics.scopes.join(', ')}</p>
      )}
    </div>
  )
}

function WebhookSetupGuide({ webhookUrl, onClose }: { webhookUrl: string; onClose: () => void }) {
  return (
    <div className="bg-[#351E1D]/5 border border-charcoal/30 rounded-lg p-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-semibold text-text-primary">How to get the SendGrid webhook signing key</h2>
          <p className="text-xs text-text-muted mt-1">
            SendGrid signs every event webhook with an ECDSA private key it keeps internally. To verify
            those signatures we need the matching <span className="font-semibold">public</span> key. Follow
            these steps in your SendGrid account, then paste the key into the field above and save.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-xs text-text-muted hover:text-text-primary whitespace-nowrap">
          Hide guide
        </button>
      </div>

      <ol className="text-sm text-text-primary space-y-3 list-decimal pl-5">
        <li>
          Sign in to SendGrid as an admin and open{' '}
          <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-stone/30">
            Settings → Mail Settings
          </span>{' '}
          (left sidebar).
        </li>
        <li>
          Find the <span className="font-semibold">Event Webhook</span> row and click its name to open it.
          Toggle <span className="font-semibold">HTTP POST URL</span> on if it isn&apos;t already, and paste
          this URL into the field:
          <code className="block mt-1 text-xs bg-white p-2 rounded border border-stone/30 break-all font-mono">
            {webhookUrl}
          </code>
        </li>
        <li>
          Pick the events you want recorded. At minimum enable <span className="font-semibold">Delivered</span>,{' '}
          <span className="font-semibold">Bounced</span>, <span className="font-semibold">Dropped</span>, and{' '}
          <span className="font-semibold">Spam Reports</span> — those are the ones we record into Recent
          Events and the ones that confirm test results. Click <span className="font-semibold">Save</span>.
        </li>
        <li>
          Scroll down to the <span className="font-semibold">Signed Event Webhook</span> section. Toggle it{' '}
          <span className="font-semibold">on</span> — SendGrid will generate a fresh ECDSA keypair and
          immediately show the public key under <span className="font-semibold">Verification Key</span>.
        </li>
        <li>
          Click the copy icon next to the verification key. The value is a long base64 string starting with
          something like <span className="font-mono text-xs">MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE…</span>.
          Copy the entire string — no surrounding quotes or extra whitespace.
        </li>
        <li>
          Paste it into the <span className="font-semibold">Webhook verification key</span> field above and
          click <span className="font-semibold">Save settings</span>. The key is stored on the server and
          isn&apos;t shown again — only a &ldquo;key is on file&rdquo; indicator.
        </li>
        <li>
          Click <span className="font-semibold">Verify webhook</span> below. The four local checks should all
          pass. If any fail, the detail line tells you what to fix.
        </li>
        <li>
          Back in SendGrid, click <span className="font-semibold">Test Your Integration</span> on the Event
          Webhook page. SendGrid will fire a signed test event at our URL — within a few seconds{' '}
          <span className="font-semibold">Signed events seen</span> in the Status card should show a
          timestamp. If instead you see a red <span className="font-semibold">Signature rejected</span> entry
          in Recent events, the pasted key doesn&apos;t match — rotate the key in SendGrid, copy the new
          value, and save again.
        </li>
      </ol>

      <div className="mt-4 pt-3 border-t border-charcoal/20 text-xs text-text-muted">
        <p className="font-semibold mb-1">Rotating the key</p>
        <p>
          If you suspect the key has leaked, click <span className="font-semibold">Rotate</span> in
          SendGrid&apos;s Signed Event Webhook panel, copy the new public key, and save it here. Until you
          save the new key, every inbound event will be rejected as{' '}
          <span className="font-semibold">Signature rejected</span>.
        </p>
      </div>
    </div>
  )
}

function VerificationResultPanel({ verification }: { verification: VerificationFlash }) {
  const borderClass = verification.passed ? 'border-[#15532E]/30 bg-[#E8F5EC]' : 'border-[#ED1C24]/30 bg-[#FDECEC]'
  const headlineClass = verification.passed ? 'text-[#15532E]' : 'text-[#ED1C24]'
  return (
    <div className={`mt-4 border rounded-lg p-4 ${borderClass}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm font-semibold ${headlineClass}`}>
          {verification.passed ? 'All local checks passed' : 'One or more checks failed'}
        </p>
        <span className="text-xs text-text-muted">Ran {verification.ran_at_display}</span>
      </div>
      <ul className="space-y-2">
        {verification.results.map((r, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span
              className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-white text-xs font-bold ${
                r.ok ? 'bg-[#15532E]' : 'bg-[#ED1C24]'
              }`}
              aria-hidden="true"
            >
              {r.ok ? '✓' : '✕'}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-primary">{r.name}</div>
              <div className="text-xs text-text-muted">{r.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function EventTypeBadge({ eventType }: { eventType: string }) {
  if (eventType === 'signature_rejected') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#ED1C24]/10 text-[#ED1C24] text-xs font-semibold">
        Signature rejected
      </span>
    )
  }
  return <span>{eventType}</span>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-text-primary mb-1">{label}</span>
      {children}
    </label>
  )
}

function Row({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-text-muted whitespace-nowrap">{label}</dt>
      <dd className={`font-semibold text-right ${ok ? 'text-[#15532E]' : 'text-[#ED1C24]'}`}>{value}</dd>
    </div>
  )
}
